import { AxiosError } from 'axios';
import network from '@/services/network';
import {
  PaginatedResponse,
  Customer,
  ApiResponse,
  InsuranceCreate,
  Insurance,
  SearchParams,
  Funding,
  CalculateSavingsResponse,
  CalculateSavingsRequest,
  ConceptProduct,
  ConceptProductPayload,
  DocumentsTable,
  HealthInsurance,
  HealthInsuranceComparisonResult,
  ActivityLog,
  BeratungsZugang,
  HubspotFlags,
} from '@/types';
import {
  ComparisonInputs,
  WithdrawalPlanInputs,
  ComparisonResult,
} from '@/types/comparisonCalculator';
import {
  DebtFreeInputs,
  DebtFreeResult,
} from '@/types/debtFreeCalculator';
import { HouseholdUpdate } from '@/store/customer/types';
import { ErrorResponse } from '@/utils';
import { mapToNumbers, normalizeData } from '@/utils/system';
import { sortBy } from 'lodash';

const getCustomers = async (params: SearchParams): Promise<PaginatedResponse<Customer>> => {
  try {
    const { data } = await network.get<PaginatedResponse<Customer>>(
      '/customers',
      {
        params,
      },
    );

    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getCustomer = async (id: string): Promise<Partial<Customer>> => {
  try {
    const {
      data: { data },
    } = await network.get<ApiResponse<Partial<Customer>>>(`/customers/${id}`);

    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

// `createCustomer` (POST /customers) ist am 07.08.2026 entfallen — mit
// CreateCustomerDialog verschwand der einzige Aufrufer. Die BE-Route
// bleibt bestehen (siehe Wiedervorlage), nur das FE ruft sie nicht mehr.

const updateCustomer = async (id: string, customer: Partial<Customer>): Promise<Partial<Customer>> => {
  try {
    const cleanedCustomer = { ...customer };

    if (
      cleanedCustomer.additional_info &&
      (typeof cleanedCustomer.additional_info === 'string' ||
        '0' in cleanedCustomer.additional_info)
    ) {
      delete cleanedCustomer.additional_info;
    }

    if (cleanedCustomer.school_years_after_17 !== undefined) {
      cleanedCustomer.school_years_after_17 = Number(
        cleanedCustomer.school_years_after_17,
      );
    }

    const {
      data: { data },
    } = await network.put<ApiResponse<Partial<Customer>>>(
      `/customers/${id}`,
      normalizeData(cleanedCustomer, [
        'id',
        'user_id',
        'hubspot_contact_id',
        'updated_at',
        'created_at',
      ]),
    );

    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getFundingsByCustomerId = async (id: string): Promise<Funding[]> => {
  try {
    const {
      data: { data },
    } = await network.get<ApiResponse<Funding[]>>(`/customers/${id}/fundings`);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getInsurances = async (customerId: string): Promise<Insurance[]> => {
  try {
    const {
      data: { data },
    } = await network.get<PaginatedResponse<Insurance>>(
      `/customers/${customerId}/insurances`,
    );

    return sortBy(data, 'id');
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const addInsurance = async (
  customerId: string,
  insurance: Partial<InsuranceCreate>,
): Promise<Partial<Insurance>> => {
  try {
    const { payment_frequency, new_payment_frequency, ...otherFields } =
      insurance;

    const processedData = {
      ...mapToNumbers(
        otherFields,
        [
          'annual_payment',
          'monthly_payment',
          'new_annual_payment',
          'new_monthly_payment',
          'credit_balance',
          'new_credit_balance',
          'roi',
          'new_roi',
        ],
        'float',
      ),
      payment_frequency,
      new_payment_frequency,
    };

    const { data } = await network.post<Partial<Insurance>>(
      `customers/${customerId}/insurances`,
      processedData,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateInsurance = async (
  customerId: string,
  insuranceId: number,
  insurance: Partial<InsuranceCreate>,
): Promise<ApiResponse<Partial<Insurance>>> => {
  try {
    const { payment_frequency, new_payment_frequency, ...otherFields } =
      insurance;

    const processedData = {
      ...mapToNumbers(
        otherFields,
        [
          'annual_payment',
          'monthly_payment',
          'new_annual_payment',
          'new_monthly_payment',
          'credit_balance',
          'new_credit_balance',
          'roi',
          'new_roi',
        ],
        'float',
      ),
      payment_frequency,
      new_payment_frequency,
    };

    const { data } = await network.put<ApiResponse<Partial<Insurance>>>(
      `customers/${customerId}/insurances/${insuranceId}`,
      processedData,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteInsurance = async (customerId: string, ids: number[]): Promise<ApiResponse<Partial<Customer>>> => {
  try {
    const { data } = await network.delete<ApiResponse<Partial<Customer>>>(
      `/customers/${customerId}/insurances/bulk-delete`,
      {
        data: {
          insurance_ids: ids,
        },
      },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const calculateSaving = async (
  customer_id: number,
  request: CalculateSavingsRequest,
  type: 'diagram' | 'children',
  signal?: AbortSignal,
): Promise<CalculateSavingsResponse[]> => {
  try {
    const { data } = await network.post<CalculateSavingsResponse[]>(
      `savings/calculate/${type}`,
      {
        customer_id,
        ...mapToNumbers(
          mapToNumbers(request, ['target_age', 'current_age']),
          ['savings_rate', 'fundings_sum', 'roi'],
          'float',
        ),
        adjustment: request.adjustment ?? [],
      },
      { signal },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getHousehold = async (customerId: string): Promise<ApiResponse<HouseholdUpdate>> => {
  try {
    const { data } = await network.get<ApiResponse<HouseholdUpdate>>(`/customers/${customerId}/household`);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateHousehold = async (customerId: string, householdData: HouseholdUpdate): Promise<ApiResponse<HouseholdUpdate>> => {
  try {
    const { data } = await network.post<ApiResponse<HouseholdUpdate>>(
      `/customers/${customerId}/household`,
      householdData,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getConceptProducts = async (customerId: string): Promise<ConceptProduct[]> => {
  try {
    const {
      data: { data },
    } = await network.get<ApiResponse<ConceptProduct[]>>(`/customers/${customerId}/concept-products`);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateConceptProduct = async (
  customerId: string,
  productId: number,
  productData: ConceptProductPayload,
): Promise<ConceptProduct> => {
  try {
    const {
      data: { data },
    } = await network.put<ApiResponse<ConceptProduct>>(
      `/customers/${customerId}/concept-products/${productId}`,
      productData,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteConceptProduct = async (customerId: string, productId: number): Promise<void> => {
  try {
    await network.delete(
      `/customers/${customerId}/concept-products/${productId}`,
    );
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * DSGVO-Loeschung (Decision #190) — hart und vollstaendig, inklusive Cases,
 * Terminen, Dateien und HubSpot-Kontakt.
 *
 * 502 heisst „abgebrochen, es wurde NICHTS geloescht" (Google/HubSpot nicht
 * erreichbar); der Grund steht in der Server-Meldung und wird durchgereicht.
 */
const deleteCustomer = async (customerId: number): Promise<void> => {
  try {
    await network.delete(`/customers/${customerId}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const addConceptProduct = async (
  customerId: string,
  product: ConceptProductPayload,
): Promise<ConceptProduct> => {
  try {
    const { data } = await network.post<ApiResponse<ConceptProduct>>(
      `/customers/${customerId}/concept-products`,
      product,
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getDocuments = async (customerId: string): Promise<DocumentsTable[]> => {
  try {
    const {
      data: { data },
    } = await network.get<ApiResponse<DocumentsTable[]>>(`/customers/${customerId}/documents`);

    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const addDocument = async (
  customerId: string,
  document: Omit<DocumentsTable, 'id' | 'comment'>,
): Promise<DocumentsTable> => {
  try {
    const {
      data: { data },
    } = await network.post<ApiResponse<DocumentsTable>>(`/customers/${customerId}/documents`, document);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateDocument = async (
  customerId: string,
  documentId: number,
  document: Partial<DocumentsTable>,
): Promise<DocumentsTable> => {
  try {
    const {
      data: { data },
    } = await network.put<ApiResponse<DocumentsTable>>(
      `/customers/${customerId}/documents/${documentId}`,
      document,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteDocument = async (customerId: string, documentId: number): Promise<void> => {
  try {
    await network.delete(`/customers/${customerId}/documents/${documentId}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const uploadDocument = async (
  customerId: string,
  file: File,
  type?: string,
  // Nachweis-Zuordnung (BER-67): legt eine Ausweis-/Nachweisdatei mit
  // nachweis_typ (+ person_ref beim Ausweis) im Dokumente-Tab ab; steuert die
  // grün-Logik im Antrags-Wizard.
  nachweis?: { nachweis_typ?: string; person_ref?: string },
): Promise<DocumentsTable> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (type) formData.append('type', type);
    if (nachweis?.nachweis_typ) formData.append('nachweis_typ', nachweis.nachweis_typ);
    if (nachweis?.person_ref) formData.append('person_ref', nachweis.person_ref);
    const {
      data: { data },
    } = await network.post<ApiResponse<DocumentsTable>>(
      `/customers/${customerId}/documents/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const downloadDocumentUrl = (customerId: string | number, documentId: number): string =>
  `/api/customers/${customerId}/documents/${documentId}/download`;

/**
 * Laedt eine Dokument-Datei als Blob ueber die authentifizierte API-Instanz.
 * Der Sanctum-Bearer-Header wird vom network-Interceptor gesetzt — ein reiner
 * <a href>-Download hingegen traegt den Header nicht und lief in 401
 * (Fix 2026-07-11). Rueckgabe ist das Blob; der Caller triggert den Download.
 */
const downloadDocument = async (
  customerId: string | number,
  documentId: number,
): Promise<Blob> => {
  try {
    const { data } = await network.get<Blob>(
      `/customers/${customerId}/documents/${documentId}/download`,
      { responseType: 'blob' },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Alle Dokumente des Kunden als ZIP (Ünal 12.10.2026). Den Dateinamen
 * („Dokumente Max Muster.zip") setzt der Server — er steht im
 * Content-Disposition-Header, bei Umlauten als `filename*=UTF-8''…`.
 */
const downloadDocumentsZip = async (
  customerId: string | number,
): Promise<{ blob: Blob; dateiname: string }> => {
  try {
    const { data, headers } = await network.get<Blob>(
      `/customers/${customerId}/documents/zip`,
      { responseType: 'blob' },
    );
    const disposition = String(headers['content-disposition'] ?? '');
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
    const einfach = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
    const dateiname = utf8 ? decodeURIComponent(utf8) : einfach ?? 'Dokumente.zip';
    return { blob: data, dateiname };
  } catch (error) {
    // Bei responseType 'blob' kommt auch die Fehlerantwort als Blob — erst
    // lesen, sonst sieht die Oberflaeche die Meldung des Servers nie
    // („Es sind keine Dateien vorhanden.").
    const antwort = (error as AxiosError).response;
    if (antwort?.data instanceof Blob) {
      try {
        antwort.data = JSON.parse(await antwort.data.text());
      } catch {
        // Kein JSON — dann bleibt es bei der allgemeinen Meldung.
      }
    }
    throw new ErrorResponse(error);
  }
};

const getHealthInsurances = async (): Promise<HealthInsurance[]> => {
  try {
    const {
      data: { data },
    } = await network.get<ApiResponse<HealthInsurance[]>>(
      '/health-insurances',
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const compareHealthInsurance = async (
  grossSalary: number,
  currentProvider: string,
): Promise<HealthInsuranceComparisonResult> => {
  try {
    const { data } = await network.post<HealthInsuranceComparisonResult>(
      '/health-insurances/compare',
      {
        gross_salary: grossSalary,
        current_provider: currentProvider,
      },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};


/**
 * Rechnet UND speichert — wie beim Diagramm-Rechner ist der Rechen-Aufruf
 * zugleich der Schreibweg (ein zweiter waere eine zweite Gelegenheit, die
 * Ablagen auseinanderlaufen zu lassen).
 *
 * `dealId` gesetzt = die Eingaben gehoeren diesem Fall und landen an ihm;
 * ohne Fall im Kontext (eigenstaendige Route, Direktlink ohne `&case=`) bleibt
 * es bei der Kunden-Ablage. Der Wert wird nur mitgesendet, wenn er da ist —
 * ein `deal_id: null` im Payload waere fuer die Validierung dasselbe, macht
 * die Absicht im Netzwerk-Log aber unleserlich.
 */
const calculateDebtFree = async (
  customerId: number,
  inputs: DebtFreeInputs,
  dealId?: number | null,
): Promise<DebtFreeResult> => {
  try {
    const { data } = await network.post<DebtFreeResult>(
      'debt-free-calculator/calculate',
      {
        ...inputs,
        customer_id: customerId,
        ...(dealId ? { deal_id: dealId } : {}),
      },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getActivityLog = async (customerId: number, page = 1): Promise<PaginatedResponse<ActivityLog>> => {
  try {
    const { data } = await network.get<PaginatedResponse<ActivityLog>>(
      `/customers/${customerId}/activity-log`,
      { params: { page } },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Letzter früherer Wert eines Kontaktfeldes aus dem Aktivitätsprotokoll —
 * `null`, wenn es keinen gibt (Zurückholen-Knopf, Ünal 12.10.2026).
 */
const getVorherigerWert = async (
  customerId: number,
  feld: string,
): Promise<{ wert: string; geaendert_at: string } | null> => {
  try {
    const { data } = await network.get<ApiResponse<{ wert: string; geaendert_at: string } | null>>(
      `/customers/${customerId}/vorheriger-wert/${feld}`,
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

interface SearchResult {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  active_deal: {
    id: number;
    topic: string | null;
    status: string | null;
  } | null;
}

// B-1 Welle 6: Manuelle Mail-Versand vom Customer
/**
 * Aufgeloester Vorlagentext fuer den Editor im SendMailDialog (08.09.2026).
 * Die Aufloesung passiert EINMAL hier; was danach abgeschickt wird, geht
 * woertlich raus.
 */
const mailEntwurf = async (
  customerId: number,
  mailTemplateId: number,
): Promise<import('@/types').MailEntwurf> => {
  try {
    const { data } = await network.post<{ data: import('@/types').MailEntwurf }>(
      `/customers/${customerId}/mail-entwurf`,
      { mail_template_id: mailTemplateId },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const sendMail = async (
  customerId: number,
  payload: import('@/types').SendMailPayload,
): Promise<{ message: string }> => {
  try {
    const { data } = await network.post<{ message: string }>(
      `/customers/${customerId}/send-mail`,
      payload,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const searchCustomersGlobal = async (
  search: string,
  signal?: AbortSignal,
): Promise<SearchResult[]> => {
  try {
    const { data } = await network.get<{ data: SearchResult[] }>(
      '/customers/search',
      { params: { search }, signal },
    );
    return data.data;
  } catch (error) {
    if ((error as Error).name === 'CanceledError') return [];
    throw new ErrorResponse(error);
  }
};


/**
 * Zutritt zur Beratungsstrecke (Entscheid Alex 17.08.2026, Teil 1).
 *
 * Antwortet mit dem Zustand, den die Schranke braucht: `offen`,
 * `reaktivierung_noetig`, `gesperrt` oder `kein_case`. Rein lesend; die
 * Autorisierung steckt serverseitig im Filter (DealPolicy::bookMeeting).
 */
const getBeratungsZugang = async (customerId: number): Promise<ApiResponse<BeratungsZugang>> => {
  try {
    const { data } = await network.get<ApiResponse<BeratungsZugang>>(
      `/customers/${customerId}/beratungs-zugang`,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * HS-Anzeigefelder des Kunden — LIVE-Nachschlag, BW speichert sie nicht
 * (docs/tasks/hs-heatedup-anzeige.md).
 *
 * Der Endpoint antwortet immer 200, auch wenn HubSpot stoert; `available`
 * trennt „nichts hinterlegt" (true + value null) von „nicht nachschaubar"
 * (false). Ein throw hier ist deshalb ein echter Netz-/Auth-Fehler, kein
 * HS-Ausfall.
 */
const getHubspotFlags = async (customerId: number): Promise<HubspotFlags> => {
  try {
    const { data } = await network.get<HubspotFlags>(
      `/customers/${customerId}/hubspot-flags`,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const customer = {
  getCustomers,
  getCustomer,
  updateCustomer,
  getVorherigerWert,
  getFundingsByCustomerId,
  getInsurances,
  addInsurance,
  updateInsurance,
  deleteInsurance,
  calculateSaving,
  getHousehold,
  updateHousehold,
  getConceptProducts,
  updateConceptProduct,
  deleteConceptProduct,
  addConceptProduct,
  getDocuments,
  addDocument,
  updateDocument,
  deleteDocument,
  uploadDocument,
  downloadDocumentUrl,
  downloadDocument,
  downloadDocumentsZip,
  getHealthInsurances,
  compareHealthInsurance,
  calculateDebtFree,
  getActivityLog,
  searchCustomersGlobal,
  mailEntwurf,
  sendMail,
  getBeratungsZugang,
  getHubspotFlags,
  deleteCustomer,
};

export type { SearchResult };

export default customer;
